import { Timestamp } from 'firebase/firestore';

import type TLocation from '@src/models/interfaces/location.interface';
import type { TLocationGroup } from '@src/models/interfaces/locationGroup.interface';
import { DateUtil } from '@src/packages/utils/dates';

type WithAuditLike = {
  createdAt?: unknown;
  updatedAt?: unknown;
};

type WithEmbeddedLocations = {
  locations?: TLocation[] | null;
  locationGroup?: TLocationGroup | null;
};

const coerceAuditValue = (value: unknown): Timestamp | null => {
  const millis = DateUtil.getTimestampMs(value);
  return millis === null ? null : Timestamp.fromMillis(millis);
};

/**
 * Location/locationGroup docs re-embedded into trigger payloads often carry
 * serialized audit maps (`{ seconds, nanoseconds }` / `{ _seconds, _nanoseconds }`
 * from Elastic hits or callable round-trips) instead of Firestore Timestamps.
 * Persisting those maps corrupts schedules/templates/executions and crashes the
 * mobile app deserializer, which casts the fields to `Timestamp?`.
 */
export class EmbeddedAuditMapper {
  /**
   * Coerces `createdAt` / `updatedAt` to a Firestore `Timestamp`. Unparseable
   * values are dropped instead of persisted: absent audit fields are safe for
   * every consumer, serialized maps are not.
   */
  static normalizeAuditTimestamps<T extends WithAuditLike>(entity: T): T {
    const normalized: WithAuditLike = { ...entity };

    if ('createdAt' in normalized) {
      const createdAt = coerceAuditValue(normalized.createdAt);
      if (createdAt) {
        normalized.createdAt = createdAt;
      } else {
        delete normalized.createdAt;
      }
    }

    if ('updatedAt' in normalized) {
      const updatedAt = coerceAuditValue(normalized.updatedAt);
      if (updatedAt) {
        normalized.updatedAt = updatedAt;
      } else {
        delete normalized.updatedAt;
      }
    }

    return normalized as T;
  }

  /**
   * Applies {@link normalizeAuditTimestamps} to a trigger's embedded
   * `locations` array and `locationGroup` object.
   */
  static normalizeTriggerEmbeddedLocations<T extends WithEmbeddedLocations>(trigger: T): T {
    const normalized: WithEmbeddedLocations = { ...trigger };

    if (Array.isArray(normalized.locations)) {
      normalized.locations = normalized.locations.map(location =>
        EmbeddedAuditMapper.normalizeAuditTimestamps(location),
      );
    }

    if (normalized.locationGroup != null && typeof normalized.locationGroup === 'object') {
      normalized.locationGroup = EmbeddedAuditMapper.normalizeAuditTimestamps(
        normalized.locationGroup,
      );
    }

    return normalized as T;
  }
}
